import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Check, ShieldCheck, Workflow,
  Accessibility, FileCog, Compass, CheckCircle2, Sparkles,
} from "lucide-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROBLEMS = [
  { n: "01", title: "Admin buried in manual work", body: "Timetabling, approvals, form-chasing and status updates eating capacity that should be on students and staff." },
  { n: "02", title: "Accessibility as an afterthought", body: "Systems that were never designed with disability, language or neurodiverse needs at the centre." },
  { n: "03", title: "Governance gaps around AI", body: "Vendor pilots quietly appearing in workflows without policy, risk assessment or audit trail." },
  { n: "04", title: "Documentation that lives in one person's head", body: "SOPs missing, undocumented, or trapped in personal inboxes and Google Docs no one else can find." },
  { n: "05", title: "Change fatigue", body: "Every previous 'AI initiative' has been a demo, a pilot or a Slack channel — never something that actually reshaped the work." },
];

const APPROACH = [
  { n: "01", title: "Listen first",   body: "Interview the staff actually doing the work — not the executives who own the budget." },
  { n: "02", title: "Map the flow",   body: "Document every handoff, decision point, tool switch and manual step in the workflow as it exists today." },
  { n: "03", title: "Score for AI",   body: "Rank each pain point by feasibility, accessibility risk, governance need and realistic time saved." },
  { n: "04", title: "Pilot small",    body: "Choose one workflow. Design it end-to-end. Prove value with real data before scaling anywhere else." },
  { n: "05", title: "Hand over well", body: "Documentation, training, escalation paths and a maintenance model — so the system runs without me." },
];

const PILOTS = [
  { id: "readiness",  icon: Compass,       n: "01", title: "AI & Workflow Readiness Audit",
    body: "A structured review of your administrative workflows, current tooling, accessibility posture and realistic AI opportunities. Deliverable: a resilience score, opportunity map and 90-day roadmap." },
  { id: "redesign",   icon: Workflow,      n: "02", title: "Administrative Process Redesign",
    body: "Pick one high-friction workflow. Rebuild it end-to-end using better process design and — only where it makes sense — AI assistance. Documented, governed, handed over." },
  { id: "access",     icon: Accessibility, n: "03", title: "Accessibility & AI Review",
    body: "Assess how AI-adjacent tools are affecting accessibility for students and staff with disability, language and neurodiverse needs. WCAG-aligned findings and remediation plan." },
  { id: "implement",  icon: FileCog,       n: "04", title: "Managed Implementation Support",
    body: "Interim leadership to actually get an AI-supported workflow live inside an institution — with the governance, communications and change support to make it stick." },
  { id: "governance", icon: ShieldCheck,   n: "05", title: "Governance & SOP Development",
    body: "Standard operating procedures, policy frameworks and audit trails for AI-supported workflows. Aligned to institutional risk, TEQSA expectations and accessibility standards." },
];

const FUTURE_PRODUCTS = [
  // "Program Resilience diagnostic" moved out of roadmap — Batch 2 §8.5. It's live at /diagnostic.
  "AI-safe SOP template library",
  "Higher-ed workflow benchmarks",
  "Accessibility × AI training module",
];

export default function AISolutions() {
  useDocumentMeta({
    title: "JJ AI Solutions · Practical AI for universities — JJ Argent",
    description:
      "Practical, responsibly-scoped AI for higher education. Workflow analysis, accessibility, governance and managed rollout. Higher-ed first.",
    path: "/ai-solutions",
  });
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        enquiry_type: "ai_solutions",
        message: form.message || "Register interest — JJ AI Solutions",
      });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid="ai-solutions-page">
      {/* Hero */}
      <section className="pt-40 pb-24 relative grain overflow-hidden">
        {/* subtle cobalt glow */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(1200px 500px at 80% 10%, rgba(61,90,254,0.12), transparent 60%), radial-gradient(700px 400px at 0% 100%, rgba(61,90,254,0.05), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex items-center gap-2 border border-[#3D5AFE] text-[#3D5AFE] px-3 py-1.5 font-display text-[11px] tracking-[0.22em] uppercase" data-testid="ai-badge">
              <Sparkles size={12} /> JJ AI Solutions
            </span>
            <span className="hidden md:inline text-[10px] tracking-[0.24em] uppercase text-[#A9A9A6]">
              A sub-brand of JJ Argent · Melbourne
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-[92px] leading-[0.94] tracking-[-0.04em] max-w-5xl">
            Practical AI{" "}<br />
            for <span className="text-[#3D5AFE]">universities.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#A9A9A6]">
            JJ AI Solutions helps university teams improve accessibility, governance and administrative
            operations using practical AI, workflow design and managed implementation support.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#register" data-testid="ai-hero-register" className="btn-cobalt">
              Register interest <ArrowRight size={14} />
            </a>
            <a href="#pilots" data-testid="ai-hero-pilots" className="btn-ghost">
              See pilot services <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="border-t border-[#343437] py-24 bg-[#151517]" data-testid="ai-positioning">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="eyebrow-cobalt">Positioning</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Not another{" "}<br />AI consultancy.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#A9A9A6] text-lg leading-relaxed">
            {/* TODO(JJ) — Batch 2 §8.6: the "framework decks and vendor demos"
                line reads as competitor negging. Left as-is pending your call;
                a softer alternative would be "This is an operationally grounded
                practice — designed for the workflows that actually keep
                universities running." (i.e. drop the first paragraph). */}
            <p>
              JJ AI Solutions is <span className="text-[#FAF9F6]">not</span> a generic AI consultancy. There
              are enough of those already — most of them selling framework decks and vendor demos.
            </p>
            <p>
              This is an operationally grounded practice. The focus is <span className="text-[#3D5AFE]">useful,
              responsible and realistic</span> implementation — designed for the workflows that actually keep
              universities running.
            </p>
            <p>
              The initial market is <span className="text-[#FAF9F6]">Australian universities</span>. The offer
              combines workflow analysis, accessibility, governance and hands-on implementation support.
            </p>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="border-t border-[#343437] py-24" data-testid="ai-problems">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow-cobalt">Problems we address</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.98] tracking-tight">
                What we{" "}<br />actually fix.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-14">
              <p className="text-[#A9A9A6] max-w-md leading-relaxed">
                The patterns we see repeat across universities, community organisations and complex
                operational teams.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#343437] border border-[#343437]">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="bg-[#0B0B0C] p-8 lg:p-10 hover:bg-[#151517] transition-colors duration-500"
                data-testid={`ai-problem-${p.n}`}
              >
                <span className="oversize text-3xl text-[#3D5AFE]">{p.n}</span>
                <h3 className="mt-6 font-display text-xl md:text-2xl tracking-tight text-[#FAF9F6] leading-tight">{p.title}</h3>
                <p className="mt-3 text-[#A9A9A6] leading-relaxed text-[15px]">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="border-t border-[#343437] py-24 bg-[#151517]" data-testid="ai-approach">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow-cobalt">Our approach</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.98] tracking-tight">
                Five steps.{" "}<br />
                <span className="text-[#3D5AFE]">No slide-deck theatre.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-14">
              <p className="text-[#A9A9A6] max-w-md leading-relaxed">
                Every engagement follows the same pattern — the details differ, the discipline doesn't.
              </p>
            </div>
          </div>
          <ol className="relative">
            <div className="hidden lg:block absolute left-[48px] top-4 bottom-4 w-px bg-[#343437]" aria-hidden="true" />
            <div className="space-y-10 lg:space-y-14">
              {APPROACH.map((s, i) => (
                <motion.li
                  key={s.n}
                  initial={{ y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55, delay: i * 0.06 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start"
                  data-testid={`ai-approach-${s.n}`}
                >
                  <div className="lg:col-span-2">
                    <div className="w-24 h-24 border border-[#343437] flex items-center justify-center bg-[#0B0B0C] relative z-10">
                      <span className="oversize text-3xl text-[#3D5AFE]">{s.n}</span>
                    </div>
                  </div>
                  <div className="lg:col-span-10 lg:pt-6">
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[#FAF9F6]">{s.title}</h3>
                    <p className="mt-3 text-[#A9A9A6] leading-relaxed max-w-3xl">{s.body}</p>
                  </div>
                </motion.li>
              ))}
            </div>
          </ol>
        </div>
      </section>

      {/* Higher Ed focus */}
      <section className="border-t border-[#343437] py-24" data-testid="ai-heded-focus">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow-cobalt">Higher education focus</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Built{" "}<br />
              <span className="text-[#3D5AFE]">for universities.</span>
            </h2>
            <p className="mt-6 text-[#A9A9A6] leading-relaxed max-w-md">
              University administration is genuinely different — governance, unions, accessibility mandates,
              student welfare, TEQSA. Off-the-shelf AI advice doesn't survive first contact.
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {[
              "13+ years inside Australian and Canadian universities",
              "Direct experience with academic governance and student unions",
              "Accessibility-first practitioner (WCAG, AODA, disability services)",
              "Understands casual/unionised workforce implications",
              "Familiar with TEQSA obligations and internal audit expectations",
              "Practical — every recommendation has been operated at scale before",
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 border border-[#343437] p-4 hover:border-[#3D5AFE] transition-colors duration-500">
                <Check size={16} className="text-[#3D5AFE] mt-1 shrink-0" />
                <span className="text-[#FAF9F6] leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pilots */}
      <section id="pilots" className="border-t border-[#343437] py-24 bg-[#151517] scroll-mt-24" data-testid="ai-pilots">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow-cobalt">Pilot services</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.98] tracking-tight">
                Five ways{" "}<br />
                <span className="text-[#3D5AFE]">to start small.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-14">
              <p className="text-[#A9A9A6] max-w-md leading-relaxed">
                Right-sized pilots that produce a documented outcome — not a demo, not a Slack channel,
                not a workshop.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PILOTS.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="border border-[#343437] bg-[#0B0B0C] p-8 lg:p-10 hover:border-[#3D5AFE] transition-colors duration-500 flex flex-col"
                data-testid={`ai-pilot-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <p.icon size={22} className="text-[#3D5AFE]" />
                  <span className="oversize text-3xl text-[#3D5AFE]">{p.n}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl md:text-3xl tracking-tight text-[#FAF9F6] leading-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-[#A9A9A6] leading-relaxed flex-1">{p.body}</p>
                <a
                  href="#register"
                  className="mt-8 inline-flex items-center gap-2 font-display text-[12px] tracking-[0.18em] uppercase text-[#3D5AFE] hover:text-[#FAF9F6] transition-colors"
                >
                  Discuss this pilot <ArrowRight size={14} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future products */}
      <section className="border-t border-[#343437] py-20" data-testid="ai-future">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow-cobalt">Future products</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight tracking-tight">
              What's coming.
            </h2>
            <p className="mt-6 text-[#A9A9A6] leading-relaxed max-w-md">
              Consulting today · scalable, self-serve products tomorrow. Every pilot informs what
              becomes a product.
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-3">
            {FUTURE_PRODUCTS.map((p) => (
              <li key={p} className="border-l-2 border-[#3D5AFE] pl-5 py-2 font-display text-lg text-[#FAF9F6]">
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Register interest */}
      <section id="register" className="border-t border-[#343437] py-24 bg-[#151517] scroll-mt-24" data-testid="ai-register">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow-cobalt">Register interest</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Explore a pilot with{" "}<br />
              <span className="text-[#3D5AFE]">your team.</span>
            </h2>
            <p className="mt-6 text-[#A9A9A6] leading-relaxed max-w-md">
              Share a short brief. I'll come back within two business days with a scoped
              starting point — no obligation.
            </p>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <div
                className="border border-[#3D5AFE] p-10 md:p-12 bg-[#0B0B0C] flex flex-col items-start gap-6"
                data-testid="ai-register-success"
              >
                <CheckCircle2 size={40} className="text-[#3D5AFE]" />
                <h3 className="font-display text-3xl leading-tight tracking-tight">Received.</h3>
                <p className="text-[#A9A9A6] max-w-lg">
                  Your interest is logged. I'll be in touch within two business days.
                </p>
                <Link to="/case-studies" className="btn-cobalt">
                  Explore case studies <ArrowUpRight size={14} />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="ai-register-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AIField label="Full name" required>
                    <input
                      type="text"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      className="ai-input"
                      data-testid="ai-form-name"
                    />
                  </AIField>
                  <AIField label="Email" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="ai-input"
                      data-testid="ai-form-email"
                    />
                  </AIField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AIField label="Institution">
                    <input
                      type="text"
                      value={form.organisation}
                      onChange={(e) => setForm((f) => ({ ...f, organisation: e.target.value }))}
                      className="ai-input"
                      placeholder="e.g. University of Melbourne"
                      data-testid="ai-form-org"
                    />
                  </AIField>
                  <AIField label="Your role">
                    <input
                      type="text"
                      value={form.role}
                      onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                      className="ai-input"
                      data-testid="ai-form-role"
                    />
                  </AIField>
                </div>
                <AIField label="What are you trying to solve?" required>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="ai-input resize-none"
                    placeholder="One workflow, one problem, one team — the more specific the better."
                    data-testid="ai-form-message"
                  />
                </AIField>
                {error && <p className="text-red-400 text-sm" data-testid="ai-form-error">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="ai-form-submit"
                  className="btn-cobalt disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Register interest"} <ArrowRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>

        <style>{`
          .ai-input {
            width: 100%;
            background: transparent;
            border: 1px solid #343437;
            color: #FAF9F6;
            padding: 0.95rem 1rem;
            font-family: 'Manrope', sans-serif;
            font-size: 0.95rem;
            transition: border-color 300ms ease;
          }
          .ai-input:focus { border-color: #3D5AFE; outline: none; }
          .ai-input::placeholder { color: #6a6a6a; }
        `}</style>
      </section>
    </main>
  );
}

function AIField({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow-cobalt block mb-2">
        {label} {required && <span className="text-[#3D5AFE]">*</span>}
      </span>
      {children}
    </label>
  );
}
